import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Pill from '../common/display/Pill';
import { Button } from '../common/ui/Button';
import { useAuth } from '../../lib/AuthContext';
import { checkUserProfileCompleted } from '../../utils/profileUtils';
import { captureAppException } from '../../utils/errorCaptureUtils';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const DashboardCTA: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const isComplete = await checkUserProfileCompleted(user.id);
        setIsProfileComplete(isComplete);
      } catch (error) {
        captureAppException(error, { 
          tags: { 
            context: 'DashboardCTA', 
            action: 'check_profile_completion',
            user_id: user.id 
          } 
        });
        console.error('Error checking profile completion:', error);
        setIsProfileComplete(false);
      } finally {
        setLoading(false);
      }
    };

    checkProfileCompletion();
  }, [user?.id]);

  if (loading) {
    // Show skeleton that matches the CTA layout
    return (
      <div className="flex flex-col justify-center p-6 bg-white rounded-2xl shadow-custom">
        <div className="bg-gray-50 rounded-xl overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="md:self-stretch flex items-end pl-6 pt-1">
              <Skeleton height={80} width={80} />
            </div>
            
            <div className="flex-1 p-6">
              <div className="mb-2">
                <Skeleton width={120} height={24} borderRadius="12px" />
              </div>
              <h2 className="mb-2">
                <Skeleton width="80%" height={28} />
              </h2>
              <p>
                <Skeleton width="60%" height={20} />
              </p>
            </div>
            
            <div className="md:self-center p-6 md:pl-0 md:py-0">
              <Skeleton width={140} height={40} borderRadius="6px" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isProfileComplete) {
    // Show profile completion CTA
    return (
      <div className="flex flex-col justify-center p-6 bg-white rounded-2xl shadow-custom">
        <div className="bg-primary-50 rounded-xl overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="md:self-stretch flex items-end pl-6 pt-1">
              <img 
                src="https://azzcpbslaipddvocsrcx.supabase.co/storage/v1/object/public/assets//cta-notification.svg"
                alt="Welcome" 
                className="h-full w-auto object-contain"
              />
            </div>
            
            <div className="flex-1 p-6">
              <div className="mb-2">
                <Pill 
                  text="Welcome to holito" 
                  variant="filled" 
                  className="bg-primary-100 text-primary-base"
                />
              </div>
              <h2 className="text-xl font-bold text-primary-base mb-2">Complete your profile to unlock more features</h2>
              <p className="text-primary">Find better events, communities and resources</p>
            </div>
            
            <div className="md:self-center p-6 md:pl-0 md:py-0">
              <Button 
                onClick={() => navigate('/register/onboarding')} 
                variant="filled" 
                color="primary" 
                size="medium"
                className="whitespace-nowrap"
              >
                Complete Profile
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center p-6 bg-white rounded-2xl shadow-custom">
      <div className="bg-amber-50 rounded-xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="md:self-stretch flex items-end pl-6 pt-1">
            <img 
              src="https://azzcpbslaipddvocsrcx.supabase.co/storage/v1/object/public/assets//cta-notification.svg"
              alt="Decommission notice" 
              className="h-full w-auto object-contain"
            />
          </div>
          
          <div className="flex-1 p-6">
            <div className="mb-2">
              <Pill 
                text="Decommission notice" 
                variant="filled" 
                className="bg-amber-100 text-amber-700"
              />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Holito Communities is being decommissioned</h2>
            <p className="text-gray-700">
              Thank you for being with us on this journey. We recommend migrating events and groups to lu.ma before the end of May 2026.
            </p>
          </div>
          
          <div className="md:self-center p-6 md:pl-0 md:py-0">
            <Button 
              onClick={() => window.open('mailto:hello@holito.app', '_blank')} 
              variant="filled" 
              color="primary" 
              size="medium"
              className="whitespace-nowrap"
            >
              Request exports
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCTA; 